import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ChurnRiskList = ({ predictions, onRefresh }) => {
  const [filterRisk, setFilterRisk] = useState('all');

  const riskLevels = [
    { id: 'all', label: 'All Levels', color: 'text-foreground' },
    { id: 'critical', label: 'Critical', color: 'text-destructive' },
    { id: 'high', label: 'High', color: 'text-warning' },
    { id: 'medium', label: 'Medium', color: 'text-accent' },
    { id: 'low', label: 'Low', color: 'text-success' }
  ];

  const getRiskIcon = (level) => {
    switch (level) {
      case 'critical': return 'AlertCircle';
      case 'high': return 'AlertTriangle';
      case 'medium': return 'Info';
      case 'low': return 'CheckCircle';
      default: return 'Circle';
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'critical': return 'text-destructive';
      case 'high': return 'text-warning';
      case 'medium': return 'text-accent';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const filteredPredictions = filterRisk === 'all'
    ? predictions
    : predictions?.filter(p => p?.churn_risk_level === filterRisk);

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-1">Churn Risk Analysis</h2>
          <p className="text-sm text-muted-foreground">
            {filteredPredictions?.length} members {filterRisk !== 'all' ? `with ${filterRisk} risk` : 'tracked'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          iconName="RefreshCw"
        >
          Refresh
        </Button>
      </div>

      {/* Risk Level Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {riskLevels?.map((level) => (
          <button
            key={level?.id}
            onClick={() => setFilterRisk(level?.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth ${
              filterRisk === level?.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-foreground hover:bg-accent'
            }`}
          >
            {level?.label}
          </button>
        ))}
      </div>

      {/* Members List */}
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {filteredPredictions?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No members found for this risk level</p>
          </div>
        ) : (
          filteredPredictions?.map((prediction) => (
            <div
              key={prediction?.id}
              className="border border-border rounded-lg p-4 hover:border-primary transition-smooth"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon
                      name={getRiskIcon(prediction?.churn_risk_level)}
                      size={20}
                      className={getRiskColor(prediction?.churn_risk_level)}
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">
                      {prediction?.user_profiles?.full_name || 'Unknown Member'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {prediction?.user_profiles?.email}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-lg font-bold ${getRiskColor(prediction?.churn_risk_level)}`}>
                    {prediction?.churn_probability}%
                  </div>
                  <div className="text-xs text-muted-foreground capitalize">
                    {prediction?.churn_risk_level} Risk
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-background rounded-lg p-2">
                  <div className="text-xs text-muted-foreground mb-1">Last Activity</div>
                  <div className="text-sm font-semibold text-foreground">
                    {prediction?.days_since_last_activity || 0} days ago
                  </div>
                </div>
                <div className="bg-background rounded-lg p-2">
                  <div className="text-xs text-muted-foreground mb-1">Engagement Trend</div>
                  <div className="text-sm font-semibold text-foreground capitalize flex items-center gap-1">
                    <Icon
                      name={prediction?.engagement_trend === 'increasing' ? 'TrendingUp' : prediction?.engagement_trend === 'declining' ? 'TrendingDown' : 'Minus'}
                      size={14}
                    />
                    {prediction?.engagement_trend || 'stable'}
                  </div>
                </div>
              </div>

              {prediction?.recommended_actions?.actions && (
                <div className="bg-background rounded-lg p-3">
                  <div className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1">
                    <Icon name="Lightbulb" size={14} />
                    Recommended Actions
                  </div>
                  <ul className="space-y-1">
                    {prediction?.recommended_actions?.actions?.map((action, index) => (
                      <li key={index} className="text-xs text-muted-foreground flex items-start gap-2">
                        <Icon name="ChevronRight" size={12} className="mt-0.5 flex-shrink-0" />
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ChurnRiskList;